import { useCallback } from 'react';
import {
  useConfirmPromoPostPostedMutation,
  useGetPromoPostsQuery,
  usePatchPromoPostMutation,
} from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { canConfirmPostPosted, isPostEditable } from '../postActions';

interface UsePromotePostsProps {
  accountId: number;
  /** ISO 8601, inclusive. */
  from: string;
  /** ISO 8601, exclusive. */
  to: string;
}

/**
 * The posts in one window, and the three edits a post accepts.
 *
 * Every write checks the post it names first. The server answers 409 on a post
 * that is `publishing`, `posted` or `canceled`, so a stale screen must not send
 * the call at all.
 */
export function usePromotePosts({ accountId, from, to }: UsePromotePostsProps) {
  const { data, isLoading, isFetching, error, refetch } = useGetPromoPostsQuery({ accountId, from, to });
  const [patchPost, { isLoading: isPatching, error: patchError }] = usePatchPromoPostMutation();
  const [confirmPosted, { isLoading: isConfirming, error: confirmError }] = useConfirmPromoPostPostedMutation();

  const posts = data?.posts;

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  /** A post the window does not hold is left to the server to judge. */
  const findPost = useCallback((postUuid: string) => posts?.find((post) => post.uuid === postUuid), [posts]);

  const saveCaption = useCallback(
    async (postUuid: string, caption: string): Promise<boolean> => {
      const post = findPost(postUuid);
      if (post !== undefined && !isPostEditable(post)) {
        return false;
      }
      const result = await patchPost({ accountId, postUuid, caption });
      return isSuccessfulResult(result);
    },
    [accountId, findPost, patchPost]
  );

  const reschedule = useCallback(
    async (postUuid: string, scheduledAt: string): Promise<boolean> => {
      const post = findPost(postUuid);
      if (post !== undefined && !isPostEditable(post)) {
        return false;
      }
      const result = await patchPost({ accountId, postUuid, scheduledAt });
      return isSuccessfulResult(result);
    },
    [accountId, findPost, patchPost]
  );

  const cancelPost = useCallback(
    async (postUuid: string): Promise<boolean> => {
      const post = findPost(postUuid);
      if (post !== undefined && !isPostEditable(post)) {
        return false;
      }
      const result = await patchPost({ accountId, postUuid, status: 'canceled' });
      return isSuccessfulResult(result);
    },
    [accountId, findPost, patchPost]
  );

  const markPosted = useCallback(
    async (postUuid: string): Promise<boolean> => {
      const post = findPost(postUuid);
      if (post !== undefined && !canConfirmPostPosted(post)) {
        return false;
      }
      const result = await confirmPosted({ accountId, postUuid });
      return isSuccessfulResult(result);
    },
    [accountId, confirmPosted, findPost]
  );

  return {
    posts,
    isLoading,
    isFetching,
    error,
    retry,
    saveCaption,
    reschedule,
    cancelPost,
    markPosted,
    isWriting: isPatching || isConfirming,
    writeError: patchError ?? confirmError,
  };
}
